import React, { useEffect, useState } from 'react';
import { AdminPanelPage } from '../AdminPanelPage';
import { Button, Card, CardBody, CardHeader, Heading, Icon, NumberDisplay } from '@aidkitorg/component-library';
import { useAccounts } from '../../Contexts/AccountsContext';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { AccountsPieChart } from './components/AccountsPieChart';
import { generateTimestampString } from '../../utils/adminPanel.utils';
import { useLocalizedStrings } from '../../Localization';
import { useVisibilityPolling } from '../../Hooks/VisibilityPolling';
import { useLocation } from 'react-router-dom';
import { AccountDetailsCard } from './components/AccountDetailsCard';
import { Account } from '@aidkitorg/aidkit/lib/admin_panel/accounts';

export type AccountWithColor = Account & {accountColor: string}

const accountKindToName = (kind: Account['kind']): string => {
  switch(kind) {
    case 'usio':
      return 'USIO (Debit)'
    case 'dwolla':
      return 'Dwolla (ACH)'
    case 'mercury':
      return 'Mercury Account'
    case 'usbank':
      return 'US Bank (Debit)'
    case 'givecard':
      return 'Givecard'
    case 'givecard_rtp':
      return 'Givecard (ACH)'
    case 'givecard_debit':
      return "Givecard (Debit)"
    case 'unknown':
      return "Unknown"
  }
}

const generateAccountColors = (count: number) => {
  const colors = []
  const sat = 65
  const light = 65

  const offset = 17
  let hue = 210

  for (let i = 0; i < count; i++) {
    colors.push(`hsl(${Math.round(hue) % 360}, ${sat}%, ${light}%)`);
    hue = (hue + offset * 3) % 360; // mix it up a bit
  }

  return colors;
}

export const AccountsPage = () => {
  const L = useLocalizedStrings()
  
  const [processedAccountsWithColors, setProcessedAccountsWithColors] = useState<Array<AccountWithColor>>([])

  const location = useLocation()
  const isOnAccountsPage = location.pathname === '/admin-panel/accounts'
    
  const { 
    totalBalance, 
    accounts, 
    accountsDataTimestamp,
    isAccountsLoading, 
    fetchAccountsOverview 
  } = useAccounts()
  
  
  useAsyncEffect(async () => {
    await fetchAccountsOverview()
  }, [])

  useVisibilityPolling({
    onImmediate: () => fetchAccountsOverview(),
    onInterval: () => fetchAccountsOverview(),
    isActive: isOnAccountsPage,
    intervalMs: 2 * 60 * 1000
  })

  useEffect(() => {
    const generatedColors = generateAccountColors(accounts.length)
    const accountsWithColors = accounts.map((account, idx) => (
      { ...account, accountColor: generatedColors[idx]}
    ))

    // Rename accounts based on the account kind
    const processedAccountsWithColors = accountsWithColors.map((account) => (
      { ...account, name: accountKindToName(account.kind)}
    ))

    setProcessedAccountsWithColors(processedAccountsWithColors)
  }, [accounts])

  const lastUpdated = (
    <div className="flex gap-2 items-center text-gray-700">
      {accountsDataTimestamp && (
        <div className="flex gap-1 items-center">
          <div>
            <Icon role="decorative" name="ClockIcon"/>
          </div>
          <div>
            {L.admin_panel.payments.last_updated}: {generateTimestampString(accountsDataTimestamp)}
          </div>
        </div>
      )}
      <div>
        <Button
          icon={<Icon role="decorative" name="ArrowPathIcon" />} 
          variant="unstyled" size="sm" 
          aria-label="Refresh data"
          onClick={async () => {
            await fetchAccountsOverview(true)
          }}
        />
      </div>
    </div>
  )

  const headerContent = (
    <>
      <div>
        <Heading level="h1" className="m-0">{L.admin_panel.accounts.accounts}</Heading>
      </div>
      <div>
        {lastUpdated}
      </div>
    </>
  )

  const totalBalanceCard = <NumberDisplay 
    title="Total Available Balance"
    isCurrency={true}
    locale="en-US"
    currency="USD"
    iconColor='blue'
    iconName='WalletIcon'
    className="w-1/4 h-full"
    value={totalBalance ? 0.01 * totalBalance : undefined } 
    loading={isAccountsLoading}
  />

  const accountsPieChartCard = <Card>
    <CardHeader title={<div className="font-medium">{L.admin_panel.accounts.payment_provider_share}</div>}/>
    <CardBody>
      <AccountsPieChart accountsWithColorData={processedAccountsWithColors} isLoading={isAccountsLoading} />
    </CardBody>
  </Card>

  const accountDetailsCards = processedAccountsWithColors.map((account, idx) => {
    return (
      <div key={idx}>
        <AccountDetailsCard account={account} />
      </div>
    )
  })

  return (
    <AdminPanelPage
      headerContent={headerContent}
    >
      <div className="flex flex-col gap-2 md:flex-row">
        <div>
          {totalBalanceCard}
        </div>
        <div className="w-full min-w-80">
          {accountsPieChartCard}
        </div>
      </div>
      <div className="pt-4">
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {accountDetailsCards}
        </div>
      </div>
    </AdminPanelPage>
  )
}